import { AnimatePresence, motion } from 'framer-motion';
import { Menu } from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { Logo } from '../components/Logo';
import { Button } from '../components/ui/Button';
import { ThemeToggle } from '../components/ui/ThemeToggle';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const NAV_LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About', end: false },
  { to: '/packages', label: 'Packages', end: false },
  { to: '/contact', label: 'Contact', end: false },
];

function NavLinks({ onNavigate, className }: { onNavigate?: () => void; className?: string }) {
  return (
    <>
      {NAV_LINKS.map((link) => (
        <NavLink
          key={link.to}
          to={link.to}
          end={link.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'text-sm font-medium transition-colors',
              isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              className,
            )
          }
        >
          {link.label}
        </NavLink>
      ))}
    </>
  );
}

/** Public marketing site's shell (P21-T02) — header + footer, no sidebar,
 * distinct from `AppShell` (the authenticated `/app` dashboard). Everyone's
 * first paint: a prospect learning about the program, picking a package,
 * or reaching the "Contact Us" lead form. */
export function PublicLayout(): JSX.Element {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-6">
          <Link to="/" className="shrink-0">
            <Logo />
          </Link>

          <nav aria-label="Primary" className="hidden lg:flex items-center gap-8">
            <NavLinks />
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <ThemeToggle />
            <Link to="/login">
              <Button variant="secondary">Client Login</Button>
            </Link>
          </div>

          <div className="flex lg:hidden items-center gap-2">
            <ThemeToggle />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Open menu"
                  className="p-2 rounded-full hover:bg-secondary transition-colors"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle>
                    <Logo />
                  </SheetTitle>
                </SheetHeader>
                <nav aria-label="Primary" className="flex flex-col gap-1 px-4 pb-6">
                  {NAV_LINKS.map((link) => (
                    <SheetClose asChild key={link.to}>
                      <NavLink
                        to={link.to}
                        end={link.end}
                        className={({ isActive }) =>
                          cn(
                            'px-3 py-2.5 rounded-xl text-sm font-medium transition-colors',
                            isActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
                          )
                        }
                      >
                        {link.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                  <SheetClose asChild>
                    <Link to="/login" className="mt-3">
                      <Button block>Client Login</Button>
                    </Link>
                  </SheetClose>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <AnimatePresence mode="wait" initial={false}>
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="flex-1"
        >
          <Outlet />
        </motion.main>
      </AnimatePresence>

      <footer className="relative bg-surface-ink text-white">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        <div className="max-w-6xl mx-auto px-6 py-12 grid gap-10 sm:grid-cols-3">
          <div className="flex flex-col gap-3">
            <Logo light />
            <p className="text-sm text-white/60 max-w-xs">
              Life Re-Engineering through DMIT-based coaching — helping you discover your inborn
              potential and rebuild your habits on a fresh scientific foundation.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-white/40">
              Explore
            </span>
            <Link to="/about" className="text-sm text-white/70 hover:text-white transition-colors">
              About
            </Link>
            <Link
              to="/packages"
              className="text-sm text-white/70 hover:text-white transition-colors"
            >
              Packages
            </Link>
            <Link
              to="/contact"
              className="text-sm text-white/70 hover:text-white transition-colors"
            >
              Contact
            </Link>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-white/40">
              Clients
            </span>
            <Link to="/login" className="text-sm text-white/70 hover:text-white transition-colors">
              Client portal login
            </Link>
          </div>
        </div>
        <div className="border-t border-white/10">
          <p className="max-w-6xl mx-auto px-6 py-4 text-xs text-white/40">
            &copy; {new Date().getFullYear()} Sanath Gamage Coaching. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
